import Grid from '@mui/material/Grid';
import { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  CombinedFamilyInfo,
  ExactAge,
  Gender,
  CustodialRelationshipType,
  CustodialRelationship,
} from '../GeneratedClient';
import { useDirectoryModel } from '../Model/DirectoryModel';
import { Warning as WarningIcon } from '@mui/icons-material';
import { ValidateDatePicker } from '../Generic/Forms/ValidateDatePicker';
import { useEthnicities } from '../Model/ConfigurationModel';
import { useParams } from 'react-router-dom';
import { useBackdrop } from '../Hooks/useBackdrop';
import { subYears } from 'date-fns';
import { useVisibleFamilies } from '../Model/Data';
import { familyLastName } from './FamilyUtils';

interface AddChildDrawerProps {
  onClose: (event: object | undefined, reason: string) => void;
}

export function AddChildDrawer({ onClose }: AddChildDrawerProps) {
  const { familyId } = useParams<{ familyId: string }>();
  const visibleFamilies = useVisibleFamilies();
  const family = visibleFamilies.find(
    (x) => x.family?.id === familyId
  ) as CombinedFamilyInfo;

  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    gender: null as Gender | null,
    dateOfBirth: null as Date | null,
    ethnicity: '',
    custodialRelationships: family
      .family!.adults!.filter((adult) => adult.item1!.active)
      .map((adult) => ({
        adult: adult.item1!,
        relationship: CustodialRelationshipType.ParentWithCustody as
          | CustodialRelationshipType
          | -1,
      })),
    notes: null as string | null,
    concerns: null as string | null,
  });
  const {
    firstName,
    lastName,
    gender,
    dateOfBirth,
    ethnicity,
    custodialRelationships,
    notes,
    concerns,
  } = fields;
  const directoryModel = useDirectoryModel();

  const ethnicities = useEthnicities();

  const [dobError, setDobError] = useState(false);

  const withBackdrop = useBackdrop();

  async function addChild() {
    await withBackdrop(async () => {
      if (firstName.length <= 0 || lastName.length <= 0) {
        alert('First and last name are required. Try again.');
      } else {
        const age = dateOfBirth == null ? null : new ExactAge();
        if (dateOfBirth != null) age!.dateOfBirth = dateOfBirth;
        await directoryModel.addChild(
          family.family?.id as string,
          firstName,
          lastName,
          gender as Gender,
          age,
          ethnicity,
          custodialRelationships
            .filter((cr) => cr.relationship !== -1)
            .map((cr) => {
              const result = new CustodialRelationship();
              result.personId = cr.adult.id;
              result.type = cr.relationship as CustodialRelationshipType;
              return result;
            }),
          notes == null ? undefined : notes,
          concerns == null ? undefined : concerns
        );
        //TODO: Error handling (start with a basic error dialog w/ request to share a screenshot, and App Insights logging)
        onClose({}, ``);
      }
    });
  }

  return (
    <Drawer
      anchor="right"
      open
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 600 },
            top: 45,
            height: 'calc(100% - 45px)',
            display: 'flex',
          },
        },
      }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 3 }}>
        <Typography id="add-child-title" variant="h6" sx={{ mb: 2 }}>
          Add Child to {familyLastName(family)}
        </Typography>
        <form noValidate autoComplete="off">
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                id="first-name"
                label="First Name"
                fullWidth
                size="small"
                value={firstName}
                onChange={(e) =>
                  setFields({ ...fields, firstName: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                required
                id="last-name"
                label="Last Name"
                fullWidth
                size="small"
                value={lastName}
                onChange={(e) =>
                  setFields({ ...fields, lastName: e.target.value })
                }
              />
            </Grid>
            <Grid size={12} sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <td>Adult</td>
                    <td>Custodial Relationship</td>
                  </tr>
                </thead>
                <tbody>
                  {custodialRelationships.map((custodialRelationship) => (
                    <tr key={custodialRelationship.adult.id}>
                      <td>
                        {custodialRelationship.adult.firstName +
                          ' ' +
                          custodialRelationship.adult.lastName}
                      </td>
                      <td>
                        <FormControl required fullWidth size="small">
                          <Select
                            id={
                              'custodial-relationship-' +
                              custodialRelationship.adult.id
                            }
                            value={custodialRelationship.relationship}
                            onChange={(e) =>
                              setFields({
                                ...fields,
                                custodialRelationships:
                                  custodialRelationships.map((cr) =>
                                    cr.adult.id ===
                                    custodialRelationship.adult.id
                                      ? {
                                          adult: custodialRelationship.adult,
                                          relationship: e.target.value as
                                            | CustodialRelationshipType
                                            | -1,
                                        }
                                      : cr
                                  ),
                              })
                            }
                          >
                            <MenuItem key="none" value={-1}>
                              None
                            </MenuItem>
                            <MenuItem
                              key="ParentWithCustody"
                              value={
                                CustodialRelationshipType.ParentWithCustody
                              }
                            >
                              Parent with custody
                            </MenuItem>
                            <MenuItem
                              key="ParentWithCourtAppointedCustody"
                              value={
                                CustodialRelationshipType.ParentWithCourtAppointedCustody
                              }
                            >
                              Parent with court-appointed custody
                            </MenuItem>
                            <MenuItem
                              key="LegalGuardian"
                              value={CustodialRelationshipType.LegalGuardian}
                            >
                              Legal guardian
                            </MenuItem>
                          </Select>
                        </FormControl>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Grid>
            <Grid size={12}>
              <Divider />
            </Grid>
            <Grid size={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Gender:</FormLabel>
                <RadioGroup
                  aria-label="genderType"
                  name="genderType"
                  row
                  value={gender == null ? null : Gender[gender]}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      gender: Gender[e.target.value as keyof typeof Gender],
                    })
                  }
                >
                  <FormControlLabel
                    value={Gender[Gender.Male]}
                    control={<Radio size="small" />}
                    label="Male"
                  />
                  <FormControlLabel
                    value={Gender[Gender.Female]}
                    control={<Radio size="small" />}
                    label="Female"
                  />
                  <FormControlLabel
                    value={Gender[Gender.SeeNotes]}
                    control={<Radio size="small" />}
                    label="See Notes"
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <ValidateDatePicker
                label="Date of birth"
                value={dateOfBirth}
                onChange={(date) => setFields({ ...fields, dateOfBirth: date })}
                minDate={subYears(new Date(), 18)}
                disableFuture
                openTo="year"
                onErrorChange={setDobError}
                textFieldProps={{
                  required: true,
                  size: 'small',
                  fullWidth: true,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="ethnicity-label">Ethnicity</InputLabel>
                <Select
                  labelId="ethnicity-label"
                  label="Ethnicity"
                  id="ethnicity"
                  value={ethnicity}
                  onChange={(e) =>
                    setFields({
                      ...fields,
                      ethnicity: e.target.value as string,
                    })
                  }
                >
                  <MenuItem key="placeholder" value="" disabled>
                    Select an ethnicity
                  </MenuItem>
                  {ethnicities.map((ethnicity) => (
                    <MenuItem key={ethnicity} value={ethnicity}>
                      {ethnicity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={12}></Grid>
            <Grid size={12}>
              <TextField
                id="concerns"
                label="Concerns"
                placeholder="Note any safety risks, allergies, etc."
                multiline
                fullWidth
                variant="outlined"
                minRows={2}
                maxRows={5}
                size="small"
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <WarningIcon />
                      </InputAdornment>
                    ),
                  },
                }}
                value={concerns == null ? '' : concerns}
                onChange={(e) =>
                  setFields({ ...fields, concerns: e.target.value })
                }
              />
            </Grid>
            <Grid size={12}>
              <TextField
                id="notes"
                label="Notes"
                placeholder="Space for any general notes"
                multiline
                fullWidth
                variant="outlined"
                minRows={2}
                maxRows={5}
                size="small"
                value={notes == null ? '' : notes}
                onChange={(e) =>
                  setFields({ ...fields, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </form>
      </Box>
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          justifyContent: 'flex-end',
          p: 2,
          pb: 'env(safe-area-inset-bottom)',
          backgroundColor: 'background.paper',
        }}
      >
        <Button onClick={() => onClose(undefined, 'cancel')} color="secondary">
          Cancel
        </Button>
        <Button
          onClick={addChild}
          variant="contained"
          color="primary"
          disabled={!dateOfBirth || dobError}
        >
          Add to Family
        </Button>
      </Box>
    </Drawer>
  );
}

export { AddChildDrawer as AddChildDialog };
